import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import Toast from "../components/Toast";
import ConfirmDialog from "../components/ConfirmDialog";
import LoaderBackdrop from "../components/LoaderBackdrop";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { httpsCallable } from "firebase/functions";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from "firebase/auth";
import { functions } from "../firebase/config";
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolid } from "@heroicons/react/24/solid";

// ─── Types ────────────────────────────────────────────────────────────────────

type ToastType = "success" | "error" | "warning" | "info";

// Which method's setup flow is currently open
type ActiveFlow = null | "email" | "authenticator";

// Sub-phases per method
type EmailPhase = "idle" | "missing_email" | "sending" | "code_entry";
type TotpPhase = "idle" | "generating" | "qr" | "verifying";

const RESEND_COOLDOWN = 30;

// ─── Helper: section card ─────────────────────────────────────────────────────

const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = "",
}) => (
  <div className={`bg-base-200 rounded-xl p-4 ${className}`}>{children}</div>
);

// ─── Component ───────────────────────────────────────────────────────────────

const TwoFaSettings: React.FC = () => {
  const { userData, currentUser, refreshUserData } = useAuth();

  // ── Derived 2FA state from userData ─────────────────────────────────────────
  // Support both new schema (twoFactorMethods) and legacy (twoFactorMethod)
  const getTwoFaMethods = () => {
    const d = userData as any;
    if (d?.twoFactorMethods) {
      return {
        email: !!d.twoFactorMethods.email,
        authenticator: !!d.twoFactorMethods.authenticator,
      };
    }
    // Legacy fallback
    if (d?.twoFactorEnabled && d?.twoFactorMethod) {
      return {
        email: d.twoFactorMethod === "email",
        authenticator: d.twoFactorMethod === "authenticator",
      };
    }
    return { email: false, authenticator: false };
  };

  const initialMethods = getTwoFaMethods();

  const [localEmailEnabled, setLocalEmailEnabled] = useState(initialMethods.email);
  const [localAuthEnabled, setLocalAuthEnabled] = useState(initialMethods.authenticator);
  const masterEnabled = localEmailEnabled || localAuthEnabled;

  useEffect(() => {
    const m = getTwoFaMethods();
    setLocalEmailEnabled(m.email);
    setLocalAuthEnabled(m.authenticator);
  }, [userData]);

  // ── Flow state ───────────────────────────────────────────────────────────────
  const [activeFlow, setActiveFlow] = useState<ActiveFlow>(null);
  const [emailPhase, setEmailPhase] = useState<EmailPhase>("idle");
  const [totpPhase, setTotpPhase] = useState<TotpPhase>("idle");
  const [loading, setLoading] = useState(false);

  // Email flow
  const [emailCode, setEmailCode] = useState("");
  const [missingEmail, setMissingEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // TOTP flow
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [totpToken, setTotpToken] = useState("");
  const [showManualSecret, setShowManualSecret] = useState(false);

  // Disable confirm
  const [confirmDisable, setConfirmDisable] = useState<null | "email" | "authenticator" | "all">(null);
  const [reauthFor, setReauthFor] = useState<null | "email" | "authenticator" | "all">(null);
  const [reauthPassword, setReauthPassword] = useState("");

  // Toast
  const [toast, setToast] = useState<{ open: boolean; type: ToastType; message: string }>({
    open: false, type: "success", message: "",
  });

  useEffect(() => () => { if (resendTimerRef.current) clearInterval(resendTimerRef.current); }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const showToast = (type: ToastType, message: string) =>
    setToast({ open: true, type, message });

  const startResendTimer = () => {
    setResendCooldown(RESEND_COOLDOWN);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    resendTimerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(resendTimerRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const resetEmailFlow = () => {
    setEmailPhase("idle");
    setEmailCode("");
    setMissingEmail("");
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    setResendCooldown(0);
  };

  const resetTotpFlow = () => {
    setTotpPhase("idle");
    setTotpToken("");
    setQrDataUrl(null);
    setTotpSecret(null);
    setShowManualSecret(false);
  };

  const closeFlow = () => {
    setActiveFlow(null);
    resetEmailFlow();
    resetTotpFlow();
  };

  const fnError = (error: any): string => {
    const code: string = error?.code ?? "";
    const message: string = error?.message ?? "";
    if (code.includes("invalid-argument")) return "Invalid code. Please try again.";
    if (code.includes("deadline-exceeded")) return "Code expired. Please request a new one.";
    if (code.includes("not-found") && !message.includes("email")) return "No code found. Please request a new one.";
    if (code.includes("unauthenticated")) return "Session expired. Please refresh.";
    return message || "Something went wrong. Please try again.";
  };

  // ── Email setup flow ─────────────────────────────────────────────────────────

  const startEmailSetup = async () => {
    setActiveFlow("email");
    setEmailPhase("sending");
    setLoading(true);
    try {
      const fn = httpsCallable(functions, "sendTwoFactorEmailCode");
      await fn({});
      startResendTimer();
      setEmailPhase("code_entry");
      showToast("info", "A verification code has been sent to your email.");
    } catch (error: any) {
      const code: string = error?.code ?? "";
      const message: string = error?.message ?? "";
      if (code.includes("not-found") && message.includes("email")) {
        setEmailPhase("missing_email");
      } else {
        showToast("error", fnError(error));
        closeFlow();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!missingEmail.trim() || !currentUser) return;
    setSavingEmail(true);
    try {
      await setDoc(
        doc(db, "users", currentUser.uid, "private", "data"),
        { email: missingEmail.trim().toLowerCase() },
        { merge: true }
      );
      setMissingEmail("");
      await startEmailSetup();
    } catch {
      showToast("error", "Failed to save email. Please try again.");
    } finally {
      setSavingEmail(false);
    }
  };

  const handleResendEmailCode = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      const fn = httpsCallable(functions, "sendTwoFactorEmailCode");
      await fn({});
      startResendTimer();
      showToast("success", "A new code has been sent.");
    } catch (error: any) {
      showToast("error", fnError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailCode.trim()) return;
    setLoading(true);
    try {
      const fn = httpsCallable(functions, "verifyTwoFactorEmailSetup");
      await fn({ code: emailCode.trim() });
      await refreshUserData();

      setLocalEmailEnabled(true);
      closeFlow();
      showToast("success", "Email verification enabled.");
    } catch (error: any) {
      showToast("error", fnError(error));
    } finally {
      setLoading(false);
    }
  };

  // ── TOTP setup flow ──────────────────────────────────────────────────────────

  const startTotpSetup = async () => {
    setActiveFlow("authenticator");
    setTotpPhase("generating");
    setLoading(true);
    try {
      const fn = httpsCallable(functions, "generateTotpSecret");
      const result = await fn({}) as any;
      setQrDataUrl(result.data.qrDataUrl);
      setTotpSecret(result.data.secret);
      setTotpPhase("qr");
    } catch {
      showToast("error", "Failed to generate QR code. Please try again.");
      closeFlow();
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotpToken = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!totpToken.trim()) return;
    setTotpPhase("verifying");
    setLoading(true);
    try {
      const fn = httpsCallable(functions, "verifyAndEnableTotpSetup");
      await fn({ token: totpToken.trim() });
      await refreshUserData();

      setLocalAuthEnabled(true);
      closeFlow();
      showToast("success", "Authenticator app enabled.");
    } catch (error: any) {
      setTotpPhase("qr");
      showToast("error", fnError(error));
    } finally {
      setLoading(false);
    }
  };

  // ── Disable a method ─────────────────────────────────────────────────────────

  const usesPassword = currentUser?.providerData?.some((p) => p.providerId === "password") ?? false;

  // The disable callables accept only a sign-in from the last few minutes.
  const reauthenticate = async (password?: string) => {
    if (!currentUser) throw new Error("Not signed in");
    if (usesPassword) {
      if (!password || !currentUser.email) throw new Error("Password required");
      await reauthenticateWithCredential(currentUser, EmailAuthProvider.credential(currentUser.email, password));
      return;
    }
    const providerId = currentUser.providerData[0]?.providerId;
    if (providerId === "google.com") {
      await reauthenticateWithPopup(currentUser, new GoogleAuthProvider());
    } else if (providerId === "apple.com") {
      await reauthenticateWithPopup(currentUser, new OAuthProvider("apple.com"));
    } else {
      throw new Error("Please sign out and sign in again, then retry.");
    }
  };

  const handleDisableMethod = async (method: "email" | "authenticator" | "all", password?: string) => {
    setLoading(true);
    try {
      await reauthenticate(password);

      const newEmail = method === "all" ? false : method === "email" ? false : localEmailEnabled;
      const newAuth = method === "all" ? false : method === "authenticator" ? false : localAuthEnabled;

      if (!newEmail && !newAuth) {
        await httpsCallable(functions, "disableTwoFactor")({});
      } else {
        await httpsCallable(functions, "disableTwoFactorMethod")({ method });
      }

      await refreshUserData();
      setLocalEmailEnabled(newEmail);
      setLocalAuthEnabled(newAuth);

      showToast(
        "success",
        method === "all"
          ? "Two-factor authentication disabled."
          : `${method === "email" ? "Email" : "Authenticator"} 2FA disabled.`
      );
    } catch {
      showToast("error", "Failed to disable. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      />

      <LoaderBackdrop open={loading && (emailPhase === "sending" || totpPhase === "generating")} />

      {/* Confirm disable dialogs */}
      <ConfirmDialog
        open={confirmDisable !== null}
        title={
          confirmDisable === "all"
            ? "Disable Two-Factor Authentication"
            : `Disable ${confirmDisable === "email" ? "Email" : "Authenticator"} 2FA`
        }
        message={
          confirmDisable === "all"
            ? "This will disable all 2FA methods. Your account will be less secure."
            : `Remove ${confirmDisable === "email" ? "email" : "authenticator app"} as a sign-in verification method?`
        }
        loading={loading}
        onConfirm={async () => {
          const method = confirmDisable!;
          setConfirmDisable(null);
          if (usesPassword) {
            setReauthPassword("");
            setReauthFor(method);
          } else {
            await handleDisableMethod(method);
          }
        }}
        onCancel={() => setConfirmDisable(null)}
      />

      {reauthFor !== null && (
        <div className="modal modal-open">
          <form
            className="modal-box space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              const method = reauthFor;
              const password = reauthPassword;
              setReauthFor(null);
              setReauthPassword("");
              await handleDisableMethod(method, password);
            }}
          >
            <h3 className="font-bold text-lg">Confirm it's you</h3>
            <p className="text-sm text-base-content/70">Enter your password to change two-factor authentication.</p>
            <input
              type="password"
              className="input input-bordered w-full"
              autoComplete="current-password"
              value={reauthPassword}
              onChange={(e) => setReauthPassword(e.target.value)}
              autoFocus
            />
            <div className="modal-action">
              <button type="button" className="btn" onClick={() => { setReauthFor(null); setReauthPassword(""); }}>
                Cancel
              </button>
              <button type="submit" className="btn btn-error" disabled={!reauthPassword}>
                Disable
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-base-100 rounded-lg p-6 space-y-6">
        {/* ── Header ── */}
        <div className="flex items-center gap-2">
          {masterEnabled ? (
            <ShieldCheckIcon className="w-5 h-5 text-green-500" />
          ) : (
            <ShieldExclamationIcon className="w-5 h-5 text-yellow-500" />
          )}
          <h3 className="text-lg font-bold text-base-content">Two-Factor Authentication</h3>
        </div>

        <p className="text-sm text-base-content/70 -mt-4">
          {masterEnabled
            ? "Your account has an extra layer of security. Configure or remove methods below."
            : "Add extra security by enabling one or more verification methods below."}
        </p>

        {/* ── Email method card ── */}
        <MethodCard
          icon={<EnvelopeIcon className="w-5 h-5 text-base-content/70" />}
          title="Email Code"
          description="Receive a one-time code to your registered email address"
          enabled={localEmailEnabled}
          flowActive={activeFlow === "email"}
          onSetup={startEmailSetup}
          onDisable={() => setConfirmDisable("email")}
          onCancel={closeFlow}
          loading={loading && activeFlow === "email"}
        >
          {/* Email sub-phases */}
          {activeFlow === "email" && emailPhase === "missing_email" && (
            <form onSubmit={handleSaveEmail} className="space-y-3 mt-3">
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-sm text-yellow-700 dark:text-yellow-300">
                No email address on file. Please enter your email to continue.
              </div>
              <div className="new-form-group w-full">
                <input
                  className="form-element"
                  id="missing-email"
                  type="email"
                  autoFocus
                  required
                  value={missingEmail}
                  onChange={(e) => setMissingEmail(e.target.value)}
                  disabled={savingEmail}
                  placeholder="Enter your email address"
                />
                <label htmlFor="missing-email" className="text-base-content/80">Email Address</label>
              </div>
              <button
                type="submit"
                disabled={savingEmail || !missingEmail.trim()}
                className="w-full bg-primary text-primary-content font-medium py-2.5 rounded-xl disabled:opacity-50 hover:opacity-90"
              >
                {savingEmail ? <Spinner label="Saving…" /> : "Continue"}
              </button>
            </form>
          )}

          {activeFlow === "email" && emailPhase === "sending" && (
            <div className="flex items-center gap-2 mt-3 text-sm text-base-content/60">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
              Sending verification code…
            </div>
          )}

          {activeFlow === "email" && emailPhase === "code_entry" && (
            <form onSubmit={handleVerifyEmailCode} className="space-y-3 mt-3">
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl text-sm text-blue-700 dark:text-blue-300">
                A 6-digit code has been sent to your email. It expires in 10 minutes.
              </div>
              <div className="new-form-group w-full">
                <input
                  className="form-element tracking-[0.4em] text-center text-lg"
                  id="email-verify-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))}
                  disabled={loading}
                  placeholder="000000"
                />
                <label htmlFor="email-verify-code" className="text-base-content/80">Verification Code</label>
              </div>
              <button
                type="submit"
                disabled={loading || emailCode.length !== 6}
                className="w-full bg-primary text-primary-content font-medium py-2.5 rounded-xl disabled:opacity-50 hover:opacity-90"
              >
                {loading ? <Spinner label="Verifying…" /> : "Enable Email 2FA"}
              </button>
              <div className="text-center">
                {resendCooldown > 0 ? (
                  <p className="text-sm text-base-content/50">Resend in {resendCooldown}s</p>
                ) : (
                  <button type="button" onClick={handleResendEmailCode} disabled={loading} className="text-sm text-base-content/70 hover:text-base-content">
                    Didn't receive a code? Resend
                  </button>
                )}
              </div>
            </form>
          )}
        </MethodCard>

        {/* ── Authenticator method card ── */}
        <MethodCard
          icon={<DevicePhoneMobileIcon className="w-5 h-5 text-base-content/70" />}
          title="Authenticator App"
          description="Use Google Authenticator, Authy, or any TOTP app"
          enabled={localAuthEnabled}
          flowActive={activeFlow === "authenticator"}
          onSetup={startTotpSetup}
          onDisable={() => setConfirmDisable("authenticator")}
          onCancel={closeFlow}
          loading={loading && activeFlow === "authenticator"}
        >
          {activeFlow === "authenticator" && totpPhase === "generating" && (
            <div className="flex items-center gap-2 mt-3 text-sm text-base-content/60">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
              Generating QR code…
            </div>
          )}

          {activeFlow === "authenticator" && (totpPhase === "qr" || totpPhase === "verifying") && (
            <form onSubmit={handleVerifyTotpToken} className="space-y-4 mt-3">
              <p className="text-sm font-medium text-base-content">Step 1 — Scan this QR code</p>
              <p className="text-xs text-base-content/60">
                Works with Google Authenticator, Authy, Microsoft Authenticator, and any TOTP app.
              </p>

              {qrDataUrl && (
                <div className="flex justify-center">
                  <div className="p-3 bg-white rounded-xl inline-block shadow-sm">
                    <img src={qrDataUrl} alt="Scan with your authenticator app" className="w-44 h-44" />
                  </div>
                </div>
              )}

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowManualSecret((v) => !v)}
                  className="text-xs text-base-content/60 hover:text-base-content/80"
                >
                  {showManualSecret ? "Hide key" : "Can't scan? Enter key manually"}
                </button>
              </div>

              {showManualSecret && totpSecret && (
                <div className="p-3 bg-base-300 rounded-xl">
                  <p className="text-xs text-base-content/60 mb-1">Manual setup key:</p>
                  <p className="font-mono text-sm text-base-content break-all select-all tracking-wider">
                    {totpSecret}
                  </p>
                  <p className="text-xs text-base-content/50 mt-1">Type: Time-based (TOTP) · Account: PEAK^</p>
                </div>
              )}

              <div className="border-t border-base-300 pt-4 space-y-3">
                <p className="text-sm font-medium text-base-content">Step 2 — Enter the 6-digit code</p>
                <div className="new-form-group w-full">
                  <input
                    className="form-element tracking-[0.4em] text-center text-lg"
                    id="totp-token"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={totpToken}
                    onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, ""))}
                    disabled={loading}
                    placeholder="000000"
                  />
                  <label htmlFor="totp-token" className="text-base-content/80">Authenticator Code</label>
                </div>
                <button
                  type="submit"
                  disabled={loading || totpToken.length !== 6}
                  className="w-full bg-primary text-primary-content font-medium py-2.5 rounded-xl disabled:opacity-50 hover:opacity-90"
                >
                  {loading ? <Spinner label="Verifying…" /> : "Enable Authenticator 2FA"}
                </button>
              </div>
            </form>
          )}
        </MethodCard>

        {/* ── Disable all ── */}
        {masterEnabled && activeFlow === null && (
          <div className="pt-2 border-t border-base-200">
            <button
              onClick={() => setConfirmDisable("all")}
              disabled={loading}
              className="w-full text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors py-2"
            >
              Disable all two-factor authentication
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// ─── MethodCard sub-component ─────────────────────────────────────────────────

interface MethodCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  enabled: boolean;
  flowActive: boolean;
  onSetup: () => void;
  onDisable: () => void;
  onCancel: () => void;
  loading: boolean;
  children?: React.ReactNode;
}

const MethodCard: React.FC<MethodCardProps> = ({
  icon, title, description, enabled, flowActive, onSetup, onDisable, onCancel, loading, children,
}) => (
  <div className={`rounded-xl border transition-all ${enabled ? "border-green-500/40 bg-green-500/5" : "border-base-300 bg-base-200"}`}>
    <div className="flex items-center gap-3 p-4">
      <div className="p-2 bg-base-100 rounded-lg shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm text-base-content">{title}</p>
          {enabled && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
              <CheckCircleSolid className="w-3.5 h-3.5" /> Enabled
            </span>
          )}
        </div>
        <p className="text-xs text-base-content/60 mt-0.5">{description}</p>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 shrink-0">
        {flowActive ? (
          <button
            onClick={onCancel}
            disabled={loading}
            className="text-xs text-base-content/60 hover:text-base-content transition-colors px-2 py-1"
          >
            Cancel
          </button>
        ) : enabled ? (
          <button
            onClick={onDisable}
            disabled={loading}
            className="text-xs font-medium text-red-500 hover:text-red-600 transition-colors px-2 py-1"
          >
            Remove
          </button>
        ) : (
          <button
            onClick={onSetup}
            disabled={loading}
            className="text-xs font-medium text-primary hover:text-primary/80 transition-colors px-3 py-1.5 bg-primary/10 rounded-lg"
          >
            Set up
          </button>
        )}
      </div>
    </div>

    {/* Inline setup flow */}
    {flowActive && children && (
      <div className="px-4 pb-4 border-t border-base-300 pt-4">{children}</div>
    )}
  </div>
);

// ─── Spinner helper ───────────────────────────────────────────────────────────

const Spinner: React.FC<{ label: string }> = ({ label }) => (
  <span className="flex items-center justify-center gap-2">
    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-content" />
    {label}
  </span>
);

export default TwoFaSettings;